import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { interestNotificationTarget } from '../utils/tradeCenterTab';
import ListingThumb from './ListingThumb';
import { useNotificationListingImages } from '../hooks/useNotificationListingImages';
import {
  CATEGORY_ROWS, unreadCountsByCategory, formatBadgeCount, notificationImage, notificationIcon, imageLookupKey, notificationCategory,
} from '../utils/notificationDisplay';
import { placeholderFor, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  


  // Close on a real window resize (rotation, desktop window drag), where the
  // position computed at open time is stale. WIDTH changes only: on DuckDuckGo
  // for Android any touch inside the open panel closed it instantly, even a
  // still finger on the UPDATES heading (member report + owner repro,
  // 2026-10-14; Chrome, Safari and the native apps were fine). The engine is
  // the same WebView the app uses, so the difference is DuckDuckGo's own
  // wrapper, whose collapsing toolbar can resize the page area the moment a
  // touch begins, before anything visibly moves. Height-only changes are also
  // what mobile browsers report when their address bar slides away mid-scroll,
  // so ignoring them is right on every phone browser, not just that one.
  //
  // There is deliberately no document-level "press outside" listener any more.
  // The full-screen backdrop below already closes the panel on an outside tap
  // or click, and a synthetic mouse event misreporting its target was the
  // other way a touch browser could close the panel from inside it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const widthAtOpen = window.innerWidth;
    const handleResize = () => {
      if (window.innerWidth !== widthAtOpen) setIsOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // The feed shows the newest 20. Since 2026-10-20 the server keeps only that
  // many per member (functions/trimUserNotifications.js), so this cap and the
  // server's match; it still guards against a legacy account the backfill
  // missed rendering hundreds of rows.
  const recentNotifications = notifications.slice(0, 20);

  const handleNotificationClick = async (notification) => {
    // Mark as read, fire-and-forget: navigation must never wait on a
    // server-acked write. On iOS, resuming the app can leave Firestore's write
    // stream dead (see the resume-reconnect note in App.js), and an awaited
    // updateDoc here hung forever, making notification taps appear to do
    // nothing (2026-09-22). markAsRead catches its own errors, and every
    // page-level caller already invokes it un-awaited.
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Determine target URL based on notification type
    let targetUrl = '';
    switch (notification.type) {
      case 'new_message':
        // New message notifications go to the trade/purchase messages page
        if (notification.tradeId) {
          targetUrl = `/trade-messages/${notification.tradeId}`;
        } else if (notification.purchaseId) {
          targetUrl = `/purchase-messages/${notification.purchaseId}`;
        } else {
          targetUrl = '/trade-center';
        }
        break;
      case 'listing_interest':
        // Inbound interest digest (batched per listing). Multi-member digests
        // open the Interests tab, including older ones whose stored link is
        // plain /trade-center; everything else keeps its own link
        // (utils/tradeCenterTab.js, interestNotificationTarget).
        targetUrl = interestNotificationTarget(notification);
        break;
      case 'listing_comment':
      case 'comment_mention':
        // Comment notifications go to the listing with auto-scroll to comments
        if (notification.link) {
          targetUrl = notification.link;
        } else if (notification.metadata?.listingId) {
          targetUrl = `/listing/${notification.metadata.listingId}?scrollToComments=true`;
        } else {
          targetUrl = '/';
        }
        break;
      case 'new_offer':
        // New offers always go to offer page to respond
        if (notification.tradeId) {
          targetUrl = `/offer/${notification.tradeId}`;
        } else {
          targetUrl = '/trade-center';
        }
        break;
      case 'offer_status_change':
        // Check if the offer was accepted - if so, go to accepted trades page
        if (notification.metadata?.action === 'accepted') {
          targetUrl = '/accepted-trades';
        } else {
          // Countered, declined, or other status changes go to offer page
          if (notification.tradeId) {
            targetUrl = `/offer/${notification.tradeId}`;
          } else {
            targetUrl = '/trade-center';
          }
        }
        break;
      case 'offer_comment':
        if (notification.tradeId) {
          targetUrl = `/offer/${notification.tradeId}`;
        } else {
          targetUrl = '/trade-center';
        }
        break;
      case 'offer_viewed':
        // Recipient viewed the offer — open the offer page for either
        // tradeOffers (tradeId) or purchaseOffers (offerId).
        if (notification.tradeId) {
          targetUrl = `/offer/${notification.tradeId}`;
        } else if (notification.offerId) {
          targetUrl = `/purchase-offer-details/${notification.offerId}`;
        } else {
          targetUrl = '/trade-center';
        }
        break;
      case 'new_match':
      case 'match_comment':
        // match_comment carries the RECIPIENT's match path (their listing
        // first) in matchId; see functions/matchThreadNotifications.js.
        if (notification.matchId) {
          targetUrl = `/match/${notification.matchId}`;
        } else {
          targetUrl = '/matches';
        }
        break;
      case 'new_match_batch':
        targetUrl = '/matches';
        break;
      case 'want_list_match':
      case 'want_list_price_drop':
        // A pedal from the user's Wantlist was just listed, or dropped in
        // price → open that listing.
        if (notification.metadata?.listingId) {
          targetUrl = `/listing/${notification.metadata.listingId}`;
        } else {
          targetUrl = '/want-list-matches';
        }
        break;
      case 'offers_sent_bulk':
        targetUrl = '/trade-center';
        break;
      case 'step_reminder':
        // 24h progress-step reminder → open the relevant progress page.
        if (notification.tradeId) {
          targetUrl = `/trade-progress/${notification.tradeId}`;
        } else if (notification.purchaseId || notification.metadata?.purchaseId) {
          targetUrl = `/purchase-progress/${notification.purchaseId || notification.metadata.purchaseId}`;
        } else {
          targetUrl = '/trade-center';
        }
        break;
      case 'ready_to_ship':
      case 'trade_complete':
      case 'tracking_added_trade':
      case 'package_delivered_trade':
        if (notification.tradeId) {
          targetUrl = `/trade-progress/${notification.tradeId}`;
        } else {
          targetUrl = '/trade-center';
        }
        break;
      case 'purchase_complete':
      case 'tracking_added_purchase':
      case 'package_delivered_purchase':
        if (notification.metadata?.purchaseId) {
          targetUrl = `/purchase-progress/${notification.metadata.purchaseId}`;
        } else {
          targetUrl = '/trade-center';
        }
        break;
      case 'new_purchase_offer':
        // New purchase offers go to purchase offer details page
        if (notification.offerId) {
          targetUrl = `/purchase-offer-details/${notification.offerId}`;
        } else {
          targetUrl = '/trade-center';
        }
        break;
      case 'purchase_offer_status_change':
        // Check if the offer was accepted - if so, go to accepted purchases page
        if (notification.metadata?.action === 'accepted') {
          targetUrl = '/accepted-purchases';
        } else {
          // Countered, declined, or other status changes go to purchase offer details page
          if (notification.offerId) {
            targetUrl = `/purchase-offer-details/${notification.offerId}`;
          } else {
            targetUrl = '/trade-center';
          }
        }
        break;
      case 'welcome':
        // Welcome notification links to Pedalboard page
        targetUrl = '/my-pedals/pedalboard';
        break;
      case 'referral_completed':
        // Referral notification links to Profile page to see referral stats
        targetUrl = '/profile';
        break;
      case 'early_bird_earned':
        targetUrl = '/profile';
        break;
      case 'offer_expired':
        if (notification.tradeId) {
          targetUrl = `/offer/${notification.tradeId}`;
        } else if (notification.offerId) {
          targetUrl = `/purchase-offer-details/${notification.offerId}`;
        } else {
          targetUrl = '/trade-center';
        }
        break;
      case 'offer_sent':
        if (notification.tradeId) {
          targetUrl = `/offer/${notification.tradeId}`;
        } else if (notification.offerId) {
          targetUrl = `/purchase-offer-details/${notification.offerId}`;
        } else {
          targetUrl = '/trade-center';
        }
        break;
      case 'billing_invoice_issued':
      case 'billing_selling_paused': {
        // Outstanding invoice — open Stripe-hosted payment page in a new tab.
        // Fall back to /profile (where SellingDisabledBanner shows the URL) if missing.
        const hostedUrl = notification.metadata?.hostedInvoiceUrl;
        if (hostedUrl) {
          window.open(hostedUrl, '_blank', 'noopener,noreferrer');
          setIsOpen(false);
          return;
        }
        targetUrl = '/profile';
        break;
      }
      case 'billing_invoice_paid':
      case 'billing_invoice_voided':
      case 'billing_invoice_closed':
        // Closed-state confirmations — no action needed; go to profile so user
        // can verify their account status.
        targetUrl = '/profile';
        break;
      default:
        targetUrl = '/trade-center';
    }

    // Check if user is already on the target page
    if (location.pathname === targetUrl) {
      // Force page refresh to show updated content
      console.log('🔄 User already on target page, forcing refresh:', targetUrl);
      window.location.reload();
    } else {
      // Navigate to different page
      navigate(targetUrl);
    }

    setIsOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    const now = new Date();
    const notificationTime = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - notificationTime;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleToggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      // Viewport coordinates (the panel is position:fixed). Full width less a
      // gutter on phones, 380px otherwise; height fits what's below the bell.
      const rect = buttonRef.current.getBoundingClientRect();
      const width = Math.min(PANEL_WIDTH, window.innerWidth - 20);
      const top = rect.bottom + 8;
      setDropdownPosition({
        top,
        left: Math.max(10, Math.min(rect.right - width, window.innerWidth - width - 10)),
        width,
        // Never past the bottom edge (a phone in landscape has little room).
        maxHeight: Math.max(160, window.innerHeight - top - 16),
      });
    }
    setIsOpen(!isOpen);
  };

  // Older notifications predate stored photos; look up the ones that name a listing.
  const listingImages = useNotificationListingImages(recentNotifications, isOpen, (n) => !!notificationImage(n));
  const badgeText = formatBadgeCount(unreadCount);
  const categoryCounts = unreadCountsByCategory(notifications);

  // Opening a row counts as reading what it was counting. The pages behind
  // the rows only clear some of the types each row counts (e.g. Trade Center
  // never clears listing_interest or offer_expired), so without this the
  // count stayed put after the tap. Fire-and-forget, like the row taps below.
  const openCategory = (row) => {
    notifications
      .filter((n) => !n.read && notificationCategory(n) === row.key)
      .forEach((n) => markAsRead(n.id));
    setIsOpen(false);
    navigate(row.path);
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Bell. Inline styles only: Tailwind isn't loaded in this project, so
          the old className="absolute" on the badge was inert and the badge sat
          UNDER the icon, pushing the whole bell up ~28px whenever there was a
          count. The badge is now positioned over the bell's top-right corner
          and never changes the bell's size. */}
      <button
        ref={buttonRef}
        onClick={handleToggleDropdown}
        aria-label={badgeText ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        style={{
          position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#4b5563',
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {badgeText && (
          <span
            data-testid="bell-badge"
            style={{
              position: 'absolute', top: '1px', left: '50%',
              height: '20px', minWidth: '20px', padding: '0 6px', boxSizing: 'border-box',
              borderRadius: '10px', backgroundColor: '#dc2626', color: 'white',
              border: '2px solid white', fontSize: '11px', fontWeight: 800, lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Nunito, sans-serif', pointerEvents: 'none', whiteSpace: 'nowrap',
            }}
          >
            {badgeText}
          </span>
        )}
      </button>

      {isOpen && createPortal(
        <>
          {/* Dimmed backdrop. Also inline: its old Tailwind classes were inert,
              so the dim never showed. */}
          <div
            onClick={() => setIsOpen(false)}
            // touchAction none: a drag on the dimmed area must not scroll the
            // page underneath (member report 2026-10-14, with the chaining
            // fix on the panel below).
            // data-pull-refresh-opt-out: a drag anywhere on the open panel or
            // its backdrop must not pull-to-refresh the page behind it either
            // (owner, native, 2026-10-20; see hooks/usePullToRefresh.js).
            data-pull-refresh-opt-out=""
            style={{ position: 'fixed', inset: 0, zIndex: 999998, backgroundColor: 'rgba(0, 0, 0, 0.35)', touchAction: 'none' }}
          />
          <div
            ref={dropdownRef}
            role="dialog"
            aria-label="Notifications"
            data-pull-refresh-opt-out=""
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width || PANEL_WIDTH,
              maxHeight: dropdownPosition.maxHeight || 600,
              zIndex: 999999,
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e5e7eb',
              // Scrolls as a whole only when even the rows don't fit
              // (landscape); normally just the feed below scrolls.
              display: 'flex', flexDirection: 'column', overflowX: 'hidden', overflowY: 'auto',
              // Scrolling past either end of the panel (or its feed, below)
              // was handed to the page behind it, so the page scrolled under
              // the open panel (member report 2026-10-14). contain stops the
              // hand-off on both the panel and the feed.
              overscrollBehavior: 'contain',
              fontFamily: 'Nunito, sans-serif',
            }}
          >
            {/* Shortcut rows: fixed at the top, unread count per area. */}
            <div style={{ flexShrink: 0, borderBottom: '1px solid #e5e7eb' }}>
              {CATEGORY_ROWS.map((row, i) => (
                <button
                  key={row.key}
                  type="button"
                  onClick={() => openCategory(row)}
                  style={{
                    display: 'flex', alignItems: 'center', width: '100%', gap: '14px',
                    padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer',
                    borderTop: i === 0 ? 'none' : '1px solid #f3f4f6', textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                >
                  <RowIcon kind={row.key} />
                  <span style={{ flex: 1, fontSize: '17px', color: '#1f2937' }}>{row.label}</span>
                  {categoryCounts[row.key] > 0 && (
                    <span style={{
                      minWidth: '22px', height: '22px', padding: '0 6px', boxSizing: 'border-box', borderRadius: '11px',
                      backgroundColor: '#dc2626', color: 'white', fontSize: '12px', fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {formatBadgeCount(categoryCounts[row.key])}
                    </span>
                  )}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', flexShrink: 0 }}>
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              ))}
            </div>

            {/* Updates header */}
            <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px 6px' }}>
              <span style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.08em', color: '#6b7280' }}>UPDATES</span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllAsRead}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#2563eb', textDecoration: 'underline', fontFamily: 'inherit', padding: 0 }}
                >
                  Mark all as read
                </button>
              )}
            </div>

            {/* Updates feed: every notification, newest first. */}
            {/* Capped at roughly 2.5-3 updates (owner, on iOS 2026-10-12: a
                full-height list showed 4 and felt too tall); the half row
                peeking at the bottom says there's more to scroll. */}
            <div style={{ overflowY: 'auto', overscrollBehavior: 'contain', flex: '0 1 auto', maxHeight: `${FEED_MAX_HEIGHT}px`, minHeight: '120px', WebkitOverflowScrolling: 'touch' }}>
              {recentNotifications.length === 0 ? (
                <p style={{ padding: '28px 18px', textAlign: 'center', color: '#6b7280', margin: 0 }}>No notifications yet</p>
              ) : (
                recentNotifications.map((notification) => {
                  const image = notificationImage(notification) || listingImages[imageLookupKey(notification)] || null;
                  return (
                    <div
                      key={notification.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleNotificationClick(notification)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleNotificationClick(notification); }}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '14px 18px', cursor: 'pointer' }}
                    >
                      {image ? (
                        <ListingThumb
                          src={image}
                          fallbackSrc={SKETCH_PLACEHOLDER_URL}
                          alt=""
                          loading="lazy"
                          style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '10px', border: '1px solid #e5e7eb', flexShrink: 0, display: 'block' }}
                        />
                      ) : (
                        <FallbackIcon kind={notificationIcon(notification)} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: '0 0 3px 0', lineHeight: 1.3 }}>
                          {notification.title}
                        </p>
                        {notification.message && (
                          <p style={{ fontSize: '14px', color: '#374151', margin: '0 0 4px 0', lineHeight: 1.35 }}>
                            {notification.message}
                          </p>
                        )}
                        <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                          {formatTimeAgo(notification.createdAt)}
                        </p>
                      </div>
                      <span
                        aria-label={notification.read ? undefined : 'Unread'}
                        style={{
                          width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, alignSelf: 'center',
                          backgroundColor: notification.read ? 'transparent' : '#dc2626',
                        }}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

const PANEL_WIDTH = 380;
// ~2.75 typical update rows (64px thumb + padding, 2-3 text lines).
const FEED_MAX_HEIGHT = 330;

const ICON_PATHS = {
  // Shortcut rows
  // Shortcut rows use our own set rather than Reverb's tag/truck (owner,
  // 2026-10-12): a target for matches (the 🎯 match titles already use), an
  // envelope for offers, swap arrows for trades.
  matches: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12z', 'M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z'],
  offers: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],
  trades: ['M17 1l4 4-4 4', 'M3 11V9a4 4 0 0 1 4-4h14', 'M7 23l-4-4 4-4', 'M21 13v2a4 4 0 0 1-4 4H3'],
  // Feed fallbacks
  heart: ['M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z'],
  clock: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 6v6l4 2'],
  box: ['M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z', 'M3.27 6.96L12 12.01l8.73-5.05', 'M12 22.08V12'],
  star: ['M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'],
  chat: ['M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z'],
  card: ['M1 4h22v16H1z', 'M1 10h22'],
  bell: ['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0'],
};

const Icon = ({ kind, size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', flexShrink: 0 }}>
    {(ICON_PATHS[kind] || ICON_PATHS.bell).map((d) => <path key={d} d={d} />)}
  </svg>
);

const RowIcon = ({ kind }) => <Icon kind={kind} size={22} color="#4b5563" />;

// Photo-less updates: a soft gold circle (gold is the emphasis colour; blue is
// reserved for things you can tap).
const FallbackIcon = ({ kind }) => (
  <div style={{
    width: '64px', height: '64px', borderRadius: '50%', flexShrink: 0,
    backgroundColor: 'rgba(201, 162, 39, 0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <Icon kind={kind} size={26} color="#a8841a" />
  </div>
);

export default NotificationDropdown;
